import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * 可輸入搜尋的下拉欄位
 *
 * 不用原生 <datalist>：客戶數量上百時它會一口氣展開整份清單，看不完也選不動。
 * 改成「打字自動補字」——輸入「東」就把欄位補成「東方包裝股份有限公司」，
 * 補上的部分反白，按 Enter／→ 接受，繼續打字或退格則忽略。
 * 同時符合多筆時，下方只列最多 5 筆，可以用滑鼠點，
 * 也可以按 ↓／↑ 在清單裡移動、再按 Enter 選定（同名開頭的人就靠這個挑）。
 *
 * 顯示的是名稱／編號，對外仍以 id 溝通；打的字對不到唯一項目時邊框會標紅，
 * 並回傳 null，避免默默存成空值。
 */

const MAX_SUGGESTIONS = 5

export interface ComboItem {
  id: number
}

/** 比對用文字：沒給 search 就用顯示文字 */
const searchOf = <T,>(x: T, label: (x: T) => string, search?: (x: T) => string) =>
  String(search ? search(x) : label(x)).toLowerCase()

interface Props<T extends ComboItem> {
  items: T[]
  value: number | null | undefined
  onChange: (item: T | null, raw: string) => void
  /** 顯示與比對用的文字，預設取 name */
  label?: (x: T) => string
  /** 選項的次要說明（例如客戶簡稱） */
  sub?: (x: T) => string
  placeholder?: string
  disabled?: boolean
  /** 沒有輸入時是否算合法（例如「合約編號」可留空） */
  allowEmpty?: boolean
  /**
   * 額外的比對文字（預設只比對 label）。
   * 例：發包單的顯示文字是單號，但也要能打寫手名字找到它。
   */
  search?: (x: T) => string
  /** 下拉最多列幾筆，預設 5 */
  maxSuggestions?: number
}

/** 從輸入字串解析出唯一符合的項目 */
export function resolveCombo<T extends ComboItem>(
  raw: string,
  items: T[],
  label: (x: T) => string,
  search?: (x: T) => string
): T | null {
  const text = raw.trim()
  if (!text) return null
  const exact = items.filter((x) => String(label(x)) === text)
  if (exact.length === 1) return exact[0]
  const low = text.toLowerCase()
  const partial = items.filter((x) => searchOf(x, label, search).includes(low))
  return partial.length === 1 ? partial[0] : null
}

export default function Combo<T extends ComboItem>({
  items,
  value,
  onChange,
  label = (x: any) => x.name,
  sub,
  placeholder = '輸入或選擇',
  disabled,
  allowEmpty = true,
  search,
  maxSuggestions = MAX_SUGGESTIONS,
}: Props<T>) {
  const inputRef = useRef<HTMLInputElement>(null)
  /** 退格與刪除時不補字，否則使用者永遠刪不掉最後一個字 */
  const erasingRef = useRef(false)
  const current = useMemo(
    () => items.find((x) => String(x.id) === String(value)) ?? null,
    [items, value]
  )
  const [text, setText] = useState(current ? label(current) : '')
  /**
   * 使用者實際敲進去的字。補字後 text 會變成完整名稱，
   * 拿它去算候選清單就只剩一筆，其他同樣開頭的選項會消失，所以要另外記。
   */
  const [typed, setTyped] = useState('')
  const [open, setOpen] = useState(false)
  /** 鍵盤 ↓／↑ 選到清單第幾筆；-1 表示還沒用鍵盤選，Enter 就走「接受補上的字」那條路 */
  const [active, setActive] = useState(-1)

  // 外部改變 value 時（例如選了合約自動帶出客戶）同步顯示文字
  useEffect(() => {
    setText(current ? label(current) : '')
    setTyped('')
    setActive(-1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, value])

  const hit = resolveCombo(text, items, label, search)
  const missing = !allowEmpty && text.trim() === ''

  /** 目前輸入命中的項目：優先開頭相符，其次包含 */
  const matches = useMemo(() => {
    const low = (typed || text).trim().toLowerCase()
    // 沒打字就不展開任何東西
    if (!low) return []
    const starts = items.filter((x) => searchOf(x, label, search).startsWith(low))
    const rest = items.filter(
      (x) =>
        !searchOf(x, label, search).startsWith(low) &&
        searchOf(x, label, search).includes(low)
    )
    return [...starts, ...rest]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, text, items])

  /*
   * 標紅的時機：打的字一個都對不到。
   * 對到多筆（例如打寫手名字、他有兩張單）只是還沒選定，清單就開在下面，
   * 這時標紅會讓人以為打錯字。
   */
  const invalid = text.trim() !== '' && !hit && matches.length === 0

  const commit = (item: T | null = hit, raw = text) => onChange(item, raw)

  const pick = (item: T) => {
    setText(label(item))
    setTyped('')
    setOpen(false)
    setActive(-1)
    onChange(item, label(item))
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setText(next)
    setTyped(next)
    setOpen(true)
    // 清單內容變了，鍵盤選到第幾筆就不算數
    setActive(-1)
    if (erasingRef.current || !next) return

    // 補上第一個「開頭相符」的完整名稱，補的那段反白，直接繼續打字就會蓋掉
    const low = next.toLowerCase()
    const first = items.find((x) => String(label(x)).toLowerCase().startsWith(low))
    if (!first) return
    const full = String(label(first))
    if (full === next) return
    setText(full)
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(next.length, full.length))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    erasingRef.current = e.key === 'Backspace' || e.key === 'Delete'

    // ↓／↑ 在建議清單裡移動，到頭或到底就繞回去
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && listed) {
      e.preventDefault()
      setOpen(true)
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1
        if (next < 0) return shown.length - 1
        if (next >= shown.length) return 0
        return next
      })
      return
    }

    if (e.key === 'Enter' || e.key === 'ArrowRight') {
      // 已經用 ↓ 選好某一筆就直接選它，不要被自動補上的第一筆蓋掉
      if (e.key === 'Enter' && open && active >= 0 && shown[active]) {
        e.preventDefault()
        pick(shown[active])
        return
      }
      // 接受補上的建議：把游標移到最後，不要讓 Enter 順手送出表單
      const el = inputRef.current
      if (e.key === 'Enter') e.preventDefault()
      el?.setSelectionRange(text.length, text.length)
      setOpen(false)
      setActive(-1)
      commit()
    }
    if (e.key === 'Escape') { setOpen(false); setActive(-1) }
  }

  const shown = matches.slice(0, maxSuggestions)
  /*
   * 一般欄位剩一筆時自動補字已經把答案填好了，不必再展開一列。
   * 但有 search 的欄位可以用「別的文字」找（例如打寫手名字找發包單），
   * 補字補不出來，只剩一筆時仍要列出來讓人確認找到的是哪一張。
   */
  const minToShow = search ? 0 : 1
  /** 清單這一刻有沒有真的列出來——沒列出來時 ↓／↑ 不該有反應 */
  const listed = shown.length > minToShow

  return (
    <div className="combo">
      <input
        ref={inputRef}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={text}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(true)}
        // 延遲關閉，否則點選建議時會先觸發 blur 讓清單消失、點不到
        onBlur={() => { setTimeout(() => setOpen(false), 120); commit() }}
        style={
          invalid || missing
            ? { borderColor: 'var(--bad)', color: 'var(--bad)' }
            : undefined
        }
        title={invalid ? '找不到符合的項目，請從清單中選擇' : undefined}
      />
      {open && listed && (
        <ul className="combo-list">
          {shown.map((x, i) => (
            <li key={x.id}>
              <button
                type="button"
                className={i === active ? 'on' : undefined}
                // 滑鼠移過去就把鍵盤的位置一起帶過來，免得兩個反白同時出現
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(x)}
              >
                {label(x)}
                {sub?.(x) && <em>{sub(x)}</em>}
              </button>
            </li>
          ))}
          {matches.length > maxSuggestions && (
            <li className="combo-more">另有 {matches.length - maxSuggestions} 筆，再多打幾個字縮小範圍</li>
          )}
        </ul>
      )}
    </div>
  )
}
